import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
    deleteInvoiceTextTemplate,
    getInvoiceTextTemplates,
    InvoiceTextTemplate,
    saveInvoiceTextTemplate,
} from '../../utils/invoice-text-templates';
import { MissingContractClients } from './MissingContractClients';

const empty: InvoiceTextTemplate = { id: '', name: '', template: '', version: 0 };

const deleteTooltipKey = (item: InvoiceTextTemplate) => {
    if (item.builtIn) return 'builtInProtected';
    if (item.everUsed) return 'usedProtected';
    return 'delete';
};

export const InvoiceTextTemplatesOptions = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoiceTextTemplates' });
    const schema = yup.object({
        name: yup
            .string()
            .trim()
            .required(t('nameRequired'))
            .max(150, t('maxLength', { count: 150 })),
        template: yup
            .string()
            .trim()
            .required(t('textRequired'))
            .max(2000, t('maxLength', { count: 2000 }))
            .test(
                'variables',
                t('allowedVariables'),
                (value) => !/[{}]/.test((value || '').replace(/\{(branchName|period|documentNumber)\}/g, ''))
            ),
    });

    const [items, setItems] = useState<InvoiceTextTemplate[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [blocked, setBlocked] = useState(true);
    const [deleting, setDeleting] = useState<InvoiceTextTemplate | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const {
        register,
        reset,
        watch,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<InvoiceTextTemplate>({
        defaultValues: empty,
        resolver: async (values) => {
            try {
                const validated = await schema.validate(values, { abortEarly: false });
                return { values: { ...values, ...validated }, errors: {} };
            } catch (error) {
                if (!(error instanceof yup.ValidationError)) throw error;
                return {
                    values: {},
                    errors: error.inner.reduce(
                        (result, item) => ({
                            ...result,
                            [item.path || 'template']: { type: item.type || 'validation', message: item.message },
                        }),
                        {}
                    ),
                };
            }
        },
    });

    const load = () => {
        setLoading(true);
        setError('');
        getInvoiceTextTemplates()
            .then(setItems)
            .catch(() => setError(t('loadError')))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const edit = (item: InvoiceTextTemplate) => {
        reset(item);
        setBlocked(true);
        setError('');
        setOpen(true);
    };

    const needsCheck = !!watch('id') && (watch('template') || '').includes('{documentNumber}');

    const save = handleSubmit(async (item) => {
        if (needsCheck && blocked) return;
        try {
            const saved = await saveInvoiceTextTemplate(item);
            setItems((previous) =>
                [...previous.filter((row) => row.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name))
            );
            setOpen(false);
            setError('');
        } catch (e: any) {
            setError(e?.message || t('saveError'));
        }
    });

    const remove = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        setError('');
        try {
            await deleteInvoiceTextTemplate(deleting.id);
            setItems((previous) => previous.filter((item) => item.id !== deleting.id));
            setDeleting(null);
        } catch (e: any) {
            setError(e?.message || t('deleteError'));
        } finally {
            setDeleteBusy(false);
        }
    };

    const example = (watch('template') || '').replace(
        /\{(branchName|period|documentNumber)\}/g,
        (_, key: string) =>
            ({ branchName: t('exampleBranch'), period: '10.2026-11.2026', documentNumber: '123/2026' }[key] || '')
    );

    return (
        <section className='mt-4'>
            <h4 className='mb-2'>{t('title')}</h4>
            <p>{t('description')}</p>
            {error && !open && (
                <Alert variant='danger'>
                    {error}{' '}
                    <Button type='button' onClick={load}>
                        {t('reload')}
                    </Button>
                </Alert>
            )}
            <Button type='button' disabled={loading} onClick={() => edit(empty)}>
                {t('add')}
            </Button>
            {loading ? (
                <p>{t('loading')}</p>
            ) : (
                <Table responsive className='mt-3'>
                    <thead className='border-top'>
                        <tr>
                            <th className='py-2 fw-bold'>{t('name')}</th>
                            <th className='py-2 fw-bold'>{t('template')}</th>
                            <th className='py-2' />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.id}>
                                <td className='py-0 align-middle'>{item.name}</td>
                                <td className='py-0 align-middle'>{item.template}</td>
                                <td className='py-0 align-middle text-nowrap text-end'>
                                    <OverlayTrigger
                                        placement='top'
                                        overlay={<Tooltip id={`edit-template-${item.id}`}>{t('edit')}</Tooltip>}
                                    >
                                        <Button
                                            type='button'
                                            size='sm'
                                            className='my-1 py-0 px-1'
                                            variant='outline-primary'
                                            aria-label={t('edit')}
                                            onClick={() => edit(item)}
                                        >
                                            <FontAwesomeIcon icon={faPencilAlt} fixedWidth />
                                        </Button>
                                    </OverlayTrigger>{' '}
                                    <OverlayTrigger
                                        placement='top'
                                        overlay={
                                            <Tooltip id={`delete-template-${item.id}`}>
                                                {t(deleteTooltipKey(item))}
                                            </Tooltip>
                                        }
                                    >
                                            <Button
                                                type='button'
                                                size='sm'
                                                className={`my-1 py-0 px-1${item.builtIn || item.everUsed ? ' disabled' : ''}`}
                                                variant='outline-danger'
                                                aria-label={t('delete')}
                                                aria-disabled={!!(item.builtIn || item.everUsed)}
                                                style={{ pointerEvents: 'auto' }}
                                                onClick={() => {
                                                    if (item.builtIn || item.everUsed) return;
                                                    setError('');
                                                    setDeleting(item);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrashAlt} fixedWidth />
                                            </Button>
                                    </OverlayTrigger>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
            <Modal show={open} onHide={() => !isSubmitting && setOpen(false)}>
                <Form onSubmit={save}>
                    <Modal.Header closeButton={!isSubmitting}>
                        <Modal.Title>{t('editTitle')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {error && <Alert variant='danger'>{error}</Alert>}
                        <Form.Group className='mb-3' controlId='invoice-template-name'>
                            <Form.Label>{t('displayName')}</Form.Label>
                            <Form.Control
                                {...register('name')}
                                maxLength={150}
                                isInvalid={!!errors.name}
                                disabled={isSubmitting}
                            />
                            <Form.Control.Feedback type='invalid'>{errors.name?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId='invoice-template-text'>
                            <Form.Label>{t('templateText')}</Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={4}
                                {...register('template')}
                                maxLength={2000}
                                isInvalid={!!errors.template}
                                disabled={isSubmitting}
                            />
                            <Form.Control.Feedback type='invalid'>{errors.template?.message}</Form.Control.Feedback>
                            <Form.Text>{t('variablesHelp')}</Form.Text>
                        </Form.Group>
                        <p className='mt-3'>
                            <strong>{t('example')}: </strong>
                            {example}
                        </p>
                        <small>{t('periodHelp')}</small>
                        {open && needsCheck && (
                            <MissingContractClients templateId={watch('id')} onBlocked={setBlocked} />
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            type='button'
                            variant='secondary'
                            disabled={isSubmitting}
                            onClick={() => setOpen(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button type='submit' disabled={isSubmitting || (needsCheck && blocked)}>
                            {t(isSubmitting ? 'saving' : 'save')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
            <Modal show={!!deleting} onHide={() => !deleteBusy && setDeleting(null)}>
                <Modal.Header closeButton={!deleteBusy}>
                    <Modal.Title>{t('deleteTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant='danger'>{error}</Alert>}
                    {t('deleteConfirmation', { name: deleting?.name })}
                </Modal.Body>
                <Modal.Footer>
                    <Button type='button' variant='secondary' disabled={deleteBusy} onClick={() => setDeleting(null)}>
                        {t('cancel')}
                    </Button>
                    <Button type='button' variant='danger' disabled={deleteBusy} onClick={remove}>
                        {t(deleteBusy ? 'deleting' : 'deleteTemplate')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </section>
    );
};
